import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import ResourcesHero from './components/ResourcesHero';
import LearningPaths from './components/LearningPaths';
import DownloadableGuides from './components/DownloadableGuides';
import VideoTutorials from './components/VideoTutorials';
import ResourcesFAQ from './components/ResourcesFAQ';
import ResourcesNewsletter from './components/ResourcesNewsletter';
import ScrollProgressBar from '../../components/feature/ScrollProgressBar';
import BackToTop from '../../components/feature/BackToTop';

export default function ResourcesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';

  useSEO({
    title: 'Trade Resources & Guides | MtaPlannerPro - Free Invoicing Tutorials',
    description: 'Free downloadable guides, video tutorials, and expert resources for Australian tradies. Learn invoicing best practices, HVAC tips, plumbing guides, electrical safety standards, and business growth strategies.',
    keywords: 'trade guides, tradie resources, invoicing tutorials, HVAC maintenance guide, plumbing tips, electrical safety checklist, carpentry techniques, construction business tips, free trade templates, Australian tradies',
    canonical: `${siteUrl}/resources`,
    ogTitle: 'Trade Resources & Guides | MtaPlannerPro',
    ogDescription: 'Free downloadable guides, video tutorials, and expert resources for Australian tradies. Master invoicing, compliance, and grow your trade business.',
    ogUrl: `${siteUrl}/resources`,
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          '@id': `${siteUrl}/resources`,
          name: 'Trade Resources & Guides',
          description: 'Comprehensive collection of free guides, video tutorials, and expert resources for Australian tradies covering invoicing, HVAC, plumbing, electrical, and business growth.',
          url: `${siteUrl}/resources`,
          isPartOf: {
            '@type': 'WebSite',
            '@id': `${siteUrl}/#website`,
            name: 'MtaPlannerPro',
            url: siteUrl
          },
          about: {
            '@type': 'Thing',
            name: 'Trade Business Resources'
          },
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Downloadable Guides',
                description: 'PDF guides covering invoicing, compliance, and trade-specific best practices'
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Video Tutorials',
                description: 'Step-by-step video tutorials for using MtaPlannerPro and improving trade skills'
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'FAQ Section',
                description: 'Answers to frequently asked questions about invoicing and trade operations'
              }
            ]
          }
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How do I create my first invoice with MtaPlannerPro?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Simply upload a photo of your job site or manually enter the job details. Our AI will analyze the work required and generate an itemized quote with real supplier prices. You can then customize the invoice and send it directly to your client.'
              }
            },
            {
              '@type': 'Question',
              name: 'Are the material prices in quotes accurate and up-to-date?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes! We integrate directly with major Australian suppliers including Bunnings Warehouse. Prices are updated regularly to ensure your quotes reflect current market rates, helping you maintain healthy profit margins.'
              }
            },
            {
              '@type': 'Question',
              name: 'What trades does MtaPlannerPro support?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'We support all major trades including HVAC, plumbing, electrical, carpentry, painting, roofing, landscaping, tiling, concreting, and general renovation work. Our AI is trained on thousands of trade-specific jobs.'
              }
            },
            {
              '@type': 'Question',
              name: 'How does the photo-to-quote feature work?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Take a photo of the job site or existing work. Our AI analyzes the image to identify materials, estimate quantities, and calculate labor requirements. It then generates a detailed quote with itemized costs in seconds.'
              }
            },
            {
              '@type': 'Question',
              name: 'Can I customize invoice templates with my business branding?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Absolutely. You can add your company logo, choose brand colors, customize payment terms, and include your ABN and contact details. Professional templates help you stand out and build client trust.'
              }
            }
          ]
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: siteUrl
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Resources',
              item: `${siteUrl}/resources`
            }
          ]
        }
      ]
    }
  });

  useEffect(() => {
    // Staggered section reveal on page load
    const sections = document.querySelectorAll('section');
    sections.forEach((section, index) => {
      section.style.opacity = '0';
      section.style.transform = 'translate3d(0, 2rem, 0)';
      
      setTimeout(() => {
        section.style.transition = 'opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)';
        section.style.opacity = '1';
        section.style.transform = 'translate3d(0, 0, 0)';
      }, 200 + index * 100);
    });
  }, []);

  return (
    <>
      <ScrollProgressBar />
      <BackToTop />
      
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <ResourcesHero />
        <LearningPaths />
        <DownloadableGuides />
        <VideoTutorials />
        <ResourcesFAQ />
        <ResourcesNewsletter />
        <Footer />
      </div>
    </>
  );
}
